import { useCallback, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { DocumentNode } from 'graphql';

const OPTION_LIMIT = 100;

export type NameOptionRow = { name: string; ids: string[] };

export function useNameFilterOptions<TData>(
  query: DocumentNode,
  buildVariables: (keyword: string) => Record<string, unknown>,
  extractRows: (data: TData) => NameOptionRow[]
) {
  const client = useApolloClient();
  const nameToIdsRef = useRef(new Map<string, string[]>());

  const loadOptions = useCallback(
    async (keyword: string) => {
      const { data } = await client.query<TData>({
        query,
        variables: buildVariables(keyword),
        fetchPolicy: 'network-only',
      });

      const rows = extractRows(data);
      const namesInOrder: string[] = [];

      for (const row of rows) {
        // Merged, not replaced: names selected from an earlier, different
        // keyword search must keep resolving to their ids once a later
        // search's results no longer include that name.
        const existingIds = nameToIdsRef.current.get(row.name) ?? [];
        const mergedIds = Array.from(new Set([...existingIds, ...row.ids]));

        nameToIdsRef.current.set(row.name, mergedIds);

        if (!namesInOrder.includes(row.name)) {
          namesInOrder.push(row.name);
        }
      }

      return namesInOrder.map(name => ({ label: name, value: name }));
    },
    [client, query, buildVariables, extractRows]
  );

  const resolveIdsForNames = useCallback((names: string[]) => {
    return names.flatMap(name => nameToIdsRef.current.get(name) ?? []);
  }, []);

  return { loadOptions, resolveIdsForNames };
}

export function buildNameSearchPaginationVariables(keyword: string) {
  return {
    name: keyword || undefined,
    pagination: { start: 0, perPage: OPTION_LIMIT },
  };
}
